import { useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import SchoolIcon from '@mui/icons-material/School'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import NoteIcon from '@mui/icons-material/Note'
import SettingsIcon from '@mui/icons-material/Settings'
import QueueMusicIcon from '@mui/icons-material/QueueMusic'
import LogoutIcon from '@mui/icons-material/Logout'
import FeedbackIcon from '@mui/icons-material/Feedback'
import GetAppIcon from '@mui/icons-material/GetApp'
import { useTranslation } from 'react-i18next'
import { MobileSheet } from './MobileSheet'
import { FeedbackDialog } from './FeedbackDialog'
import { InstallAppButton, type InstallAppButtonHandle } from './InstallAppButton'
import { useStandaloneMode } from '../hooks/useStandaloneMode'
import { useAuth } from '../hooks/useAuth'
import { LEARN_PATH } from '../lib/learnRoutes'

const primaryTabs = [
  { labelKey: 'nav.home', icon: <HomeIcon />, path: '/' },
  { labelKey: 'nav.reader', icon: <MenuBookIcon />, path: '/reader' },
  { labelKey: 'nav.learn', icon: <SchoolIcon />, path: LEARN_PATH },
  { labelKey: 'nav.bookmarks', icon: <BookmarkIcon />, path: '/bookmarks' },
  { labelKey: 'common.more', icon: <MoreHorizIcon />, path: '__more__' },
]

const moreItems = [
  { labelKey: 'nav.playlists', icon: <QueueMusicIcon />, path: '/playlists' },
  { labelKey: 'nav.progress', icon: <TrendingUpIcon />, path: '/progress' },
  { labelKey: 'nav.notes', icon: <NoteIcon />, path: '/notes' },
  { labelKey: 'nav.settings', icon: <SettingsIcon />, path: '/settings' },
]

interface BottomNavProps {
  audioPlayerHeight?: number
}

export function BottomNav({ audioPlayerHeight = 0 }: BottomNavProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [moreOpen, setMoreOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const { t } = useTranslation()
  const installRef = useRef<InstallAppButtonHandle>(null)
  const { isStandalone } = useStandaloneMode()

  const tabs = primaryTabs
  const more = moreItems

  if (!isMobile) return null

  const getActiveTab = () => {
    const match = tabs.findIndex(
      (tab) =>
        tab.path !== '__more__' &&
        (location.pathname === tab.path ||
          (tab.path !== '/' && location.pathname.startsWith(tab.path)))
    )
    // If current route isn't one of the visible tabs, highlight "More"
    // (which is always the last entry).
    if (match === -1) return tabs.length - 1
    return match
  }

  const handleTabChange = (_: unknown, newValue: number) => {
    const tab = tabs[newValue]
    if (tab.path === '__more__') {
      setMoreOpen(true)
    } else {
      navigate(tab.path)
    }
  }

  const handleMoreNav = (path: string) => {
    setMoreOpen(false)
    navigate(path)
  }

  const handleLogout = async () => {
    setMoreOpen(false)
    await logout()
    navigate('/')
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar + 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          pb: 'env(safe-area-inset-bottom)',
          // Clear a landscape notch/rounded corner. Additive — 0px on
          // devices without a left/right inset (all current portrait use).
          pl: 'var(--safe-left, 0px)',
          pr: 'var(--safe-right, 0px)',
          transform: `translateY(-${audioPlayerHeight}px)`,
          transition: 'transform 0.2s ease',
        }}
      >
        <BottomNavigation
          value={getActiveTab()}
          onChange={handleTabChange}
          showLabels
        >
          {tabs.map((tab) => (
            <BottomNavigationAction
              key={tab.labelKey}
              label={t(tab.labelKey)}
              icon={tab.icon}
            />
          ))}
        </BottomNavigation>
      </Paper>

      <MobileSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onOpen={() => setMoreOpen(true)}
        title={t('common.more')}
      >
        <List disablePadding>
          {more.map((item) => (
            <ListItem key={item.labelKey} disablePadding>
              <ListItemButton
                onClick={() => handleMoreNav(item.path)}
                selected={location.pathname.startsWith(item.path)}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={t(item.labelKey)} />
              </ListItemButton>
            </ListItem>
          ))}
          {/* Install app — manual entry point. Click closes the sheet AND
              triggers the install handler via the ref. The actual dialog /
              snackbar are mounted OUTSIDE this MobileSheet (see below) so
              they survive the sheet's animated unmount. */}
          {!isStandalone && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  setMoreOpen(false)
                  installRef.current?.trigger()
                }}
                sx={{ borderRadius: 2, mt: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                  <GetAppIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('install.menuLabel')}
                  sx={{ '& .MuiListItemText-primary': { fontWeight: 600 } }}
                />
              </ListItemButton>
            </ListItem>
          )}
          {/* Feedback — sits above Logout because it's a more frequent and
              less-destructive action. Replaces the previously-floating FAB so
              the right edge stays clear for the reader's settings column. */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setMoreOpen(false)
                setFeedbackOpen(true)
              }}
              sx={{ borderRadius: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                <FeedbackIcon />
              </ListItemIcon>
              <ListItemText primary={t('feedback.tooltip')} sx={{ color: 'text.secondary' }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2 }}>
              <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t('nav.logout')} sx={{ color: 'text.secondary' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </MobileSheet>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* Mounted OUTSIDE MobileSheet so the install dialog/snackbar render
          correctly even when the sheet closes (which unmounts its children). */}
      <InstallAppButton ref={installRef} />
    </>
  )
}

